import { z } from 'zod';
import {
  RP_LIST_CAPS,
  RP_MAX_SECTIONS,
  SAFE_URL_RE,
  type RpLayout,
  type RpSection,
} from '../replicated-sections';
import { STOREFRONT_ICON_KEYS, type StorefrontIconKey } from '../storefront-content';

/**
 * Zod mirror of the replicated-page layout (see `../replicated-sections.ts`).
 *
 * This file is a SECURITY boundary, not just a shape check. A replicated page is
 * public and unauthenticated, and the pre-builder model validated block content
 * as `z.record(z.unknown())` — which meant a stored `href` reached `<a href>`
 * verbatim and `javascript:` was plantable by anyone who could edit a page. The
 * discriminated unions below make an unsafe link unrepresentable at write time.
 *
 * HTML-bearing fields (`richText.html`) are shape-checked here but SANITIZED
 * separately by `sanitizeRpLayout` in the API — Zod validates structure, not
 * markup safety. Both must run.
 */

// ── Leaf scalars ────────────────────────────────────────────────────────────

const shortText = z.string().max(300);
/** Paragraph-scale plain text (mediaText body, testimonial quote, FAQ answer). */
const longText = z.string().max(4000);
/** Rich HTML from the WYSIWYG. Sanitized server-side before it is stored. */
const richHtml = z.string().max(50_000);
const objectKey = z.string().max(512);
const label = z.string().max(80);

const iconKeySchema = z.enum([...STOREFRONT_ICON_KEYS] as [StorefrontIconKey, ...StorefrontIconKey[]]);

// ── Style ───────────────────────────────────────────────────────────────────

const sectionStyleSchema = z
  .object({
    bg: z.enum(['page', 'muted', 'tint', 'accent', 'dark', 'image']).optional(),
    bgImageKey: objectKey.optional(),
    overlay: z.union([z.literal(0), z.literal(20), z.literal(40), z.literal(60), z.literal(80)]).optional(),
    width: z.enum(['narrow', 'normal', 'wide', 'full']).optional(),
    padY: z.enum(['none', 'xs', 'sm', 'md', 'lg', 'xl']).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    textTone: z.enum(['auto', 'dark', 'light']).optional(),
    radius: z.enum(['none', 'sm', 'md', 'lg', 'xl']).optional(),
  })
  .strict();

// ── Links ───────────────────────────────────────────────────────────────────

/**
 * The write-time enforcement of `SAFE_URL_RE`. `javascript:`, `data:`, `vbscript:`
 * and every other scheme are rejected outright — there is no sanitize-and-keep
 * path, because a link the author can't express is a link an attacker can't
 * store.
 */
const linkTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }).strict(),
  z.object({ kind: z.literal('signup') }).strict(),
  z.object({ kind: z.literal('anchor'), sectionId: z.string().max(80) }).strict(),
  z
    .object({
      kind: z.literal('url'),
      url: z.string().max(2048).regex(SAFE_URL_RE, 'unsafe_url_scheme'),
    })
    .strict(),
  z.object({ kind: z.literal('kit'), productId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('product'), productId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('page'), slug: z.string().max(80) }).strict(),
]);

const buttonSchema = z
  .object({
    label,
    link: linkTargetSchema,
    variant: z.enum(['primary', 'outline', 'ghost', 'light']).optional(),
    size: z.enum(['sm', 'md', 'lg']).optional(),
  })
  .strict();

const mediaRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('image'), imageKey: objectKey, alt: shortText.optional() }).strict(),
  // `legacyImage` is emitted only by `legacyBlocksToSections`, never authored —
  // but it must round-trip, because the editor re-saves converted pages.
  z.object({ kind: z.literal('legacyImage'), blockId: z.string().uuid(), alt: shortText.optional() }).strict(),
  z.object({ kind: z.literal('video'), url: z.string().max(2048) }).strict(),
]);

// ── Sections ────────────────────────────────────────────────────────────────

const base = { id: z.string().min(1).max(80), enabled: z.boolean(), style: sectionStyleSchema.optional() };

const sectionSchema = z.discriminatedUnion('type', [
  // ── convert ──
  z
    .object({
      ...base,
      type: z.literal('hero'),
      config: z
        .object({
          variant: z.enum(['imageBg', 'split', 'plain']),
          eyebrow: shortText.optional(),
          heading: shortText,
          subheading: longText.optional(),
          bullets: z.array(shortText).max(RP_LIST_CAPS.heroBullets).optional(),
          media: mediaRefSchema.optional(),
          primaryCta: buttonSchema.optional(),
          secondaryCta: buttonSchema.optional(),
          minHeight: z.enum(['sm', 'md', 'lg']).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('cta'),
      config: z
        .object({
          eyebrow: shortText.optional(),
          heading: shortText,
          subheading: longText.optional(),
          primaryCta: buttonSchema,
          secondaryCta: buttonSchema.optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('packages'),
      config: z
        .object({
          heading: shortText.optional(),
          subheading: longText.optional(),
          cols: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
          source: z.literal('campaignKits'),
          showPrice: z.boolean().optional(),
          showPv: z.boolean().optional(),
          ctaLabel: label.optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('countdown'),
      config: z
        .object({
          label,
          // Same bound as the page-level timer: beyond 30 days it isn't urgency.
          hours: z.number().int().min(1).max(720),
          variant: z.enum(['bar', 'blocks']),
        })
        .strict(),
    })
    .strict(),
  // ── persuade ──
  z
    .object({
      ...base,
      type: z.literal('mediaText'),
      config: z
        .object({
          variant: z.enum(['mediaLeft', 'mediaRight']),
          media: mediaRefSchema,
          eyebrow: shortText.optional(),
          heading: shortText.optional(),
          body: longText.optional(),
          bullets: z.array(shortText).max(RP_LIST_CAPS.mediaTextBullets).optional(),
          cta: buttonSchema.optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('featureGrid'),
      config: z
        .object({
          heading: shortText.optional(),
          subheading: longText.optional(),
          cols: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
          items: z
            .array(
              z
                .object({
                  icon: iconKeySchema.optional(),
                  imageKey: objectKey.optional(),
                  title: shortText,
                  body: longText.optional(),
                })
                .strict(),
            )
            .max(RP_LIST_CAPS.featureGridItems),
          cardStyle: z.enum(['plain', 'card', 'bordered']).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('steps'),
      config: z
        .object({
          heading: shortText.optional(),
          subheading: longText.optional(),
          items: z
            .array(z.object({ title: shortText, body: longText.optional() }).strict())
            .max(RP_LIST_CAPS.stepsItems),
          layout: z.enum(['row', 'stack']).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('testimonials'),
      config: z
        .object({
          heading: shortText.optional(),
          subheading: longText.optional(),
          cols: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
          items: z
            .array(
              z
                .object({
                  quote: longText,
                  name: shortText,
                  role: shortText.optional(),
                  avatarKey: objectKey.optional(),
                  rating: z
                    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)])
                    .optional(),
                })
                .strict(),
            )
            .max(RP_LIST_CAPS.testimonialItems),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('faq'),
      config: z
        .object({
          heading: shortText.optional(),
          subheading: longText.optional(),
          items: z.array(z.object({ q: shortText, a: longText }).strict()).max(RP_LIST_CAPS.faqItems),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('stats'),
      config: z
        .object({
          heading: shortText.optional(),
          items: z
            .array(z.object({ value: label, label: shortText, icon: iconKeySchema.optional() }).strict())
            .max(RP_LIST_CAPS.statsItems),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('logoStrip'),
      config: z
        .object({
          heading: shortText.optional(),
          grayscale: z.boolean().optional(),
          items: z
            .array(z.object({ imageKey: objectKey, alt: shortText.optional() }).strict())
            .max(RP_LIST_CAPS.logoStripItems),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('gallery'),
      config: z
        .object({
          heading: shortText.optional(),
          cols: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
          items: z
            .array(z.object({ imageKey: objectKey, caption: shortText.optional() }).strict())
            .max(RP_LIST_CAPS.galleryItems),
        })
        .strict(),
    })
    .strict(),
  // ── basic ──
  z
    .object({
      ...base,
      type: z.literal('heading'),
      config: z
        .object({
          text: shortText,
          level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
          eyebrow: shortText.optional(),
          subheading: longText.optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('richText'),
      // Both optional: `html` is the WYSIWYG path, `text` the pre-WYSIWYG one a
      // converted legacy block keeps.
      config: z.object({ html: richHtml.optional(), text: richHtml.optional() }).strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('image'),
      config: z
        .object({
          media: mediaRefSchema,
          caption: shortText.optional(),
          maxWidth: z.enum(['sm', 'md', 'lg', 'full']).optional(),
          link: linkTargetSchema.optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('video'),
      config: z
        .object({
          url: z.string().max(2048),
          caption: shortText.optional(),
          aspect: z.enum(['16:9', '9:16', '4:3']).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('buttonGroup'),
      config: z
        .object({
          buttons: z.array(buttonSchema).max(RP_LIST_CAPS.buttonGroupButtons),
          layout: z.enum(['row', 'stack']).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...base,
      type: z.literal('spacer'),
      config: z.object({ height: z.enum(['sm', 'md', 'lg', 'xl']), divider: z.boolean().optional() }).strict(),
    })
    .strict(),
]);

// ── Page ────────────────────────────────────────────────────────────────────

/** HSL triplet, e.g. "9.9 85% 55.5%" — the `ThemeTokens` convention. */
const hslTriplet = z
  .string()
  .max(40)
  .regex(/^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/, 'expected an HSL triplet');

const pageThemeSchema = z
  .object({
    accent: hslTriplet.optional(),
    accentFg: hslTriplet.optional(),
    pageBg: hslTriplet.optional(),
    pageFg: hslTriplet.optional(),
    cardBg: hslTriplet.optional(),
    mutedBg: hslTriplet.optional(),
    radius: z.enum(['none', 'sm', 'md', 'lg', 'xl']).optional(),
    fontHeading: z.string().max(200).optional(),
    fontBody: z.string().max(200).optional(),
    // Stylesheet URLs injected as <link> by the renderer host — https only, so a
    // page theme can't pull script-bearing CSS off an arbitrary scheme.
    fontLinks: z.array(z.string().max(500).url().startsWith('https://')).max(4).optional(),
  })
  .strict();

export const rpSectionSchema = sectionSchema;

export const rpLayoutSchema = z
  .object({
    version: z.union([z.literal(0), z.literal(1)]),
    theme: pageThemeSchema.optional(),
    sections: z.array(sectionSchema).max(RP_MAX_SECTIONS),
  })
  .strict();

/** Body of `PUT /admin/replicated-pages/:id/layout`. */
export const saveRpLayoutSchema = z.object({ layout: rpLayoutSchema });

/**
 * Compile-time guard: the Zod schemas must stay in lockstep with the types in
 * `../replicated-sections.ts`. If either side drifts, one of these fails to
 * compile — which is the whole point of keeping the union and its schema apart.
 */
type _SectionSchemaToType = z.infer<typeof rpSectionSchema> extends RpSection ? true : never;
type _SectionTypeToSchema = RpSection extends z.infer<typeof rpSectionSchema> ? true : never;
type _LayoutSchemaToType = z.infer<typeof rpLayoutSchema> extends RpLayout ? true : never;
type _LayoutTypeToSchema = RpLayout extends z.infer<typeof rpLayoutSchema> ? true : never;
const _a: _SectionSchemaToType = true;
const _b: _SectionTypeToSchema = true;
const _c: _LayoutSchemaToType = true;
const _d: _LayoutTypeToSchema = true;
void _a;
void _b;
void _c;
void _d;
